import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../app/store';
import { fetchRequisitionDetails, RequisitionDetail } from './requisitionSlice';
import apiClient from '../../services/api';
import { fetchReferenceData } from '../../services/referenceDataCache';
import toast from 'react-hot-toast';

// Interface for category and subcategory data
interface Category {
  id: number;
  name: string;
}

interface SubCategory {
  id: number;
  name: string;
  requisition_category_id: number;
}

interface Equipment {
  id: number;
  description: string;
  serial_number?: string;
  category?: {
    name: string;
  };
  subCategory?: {
    name: string;
  };
}

interface Store {
  id: number;
  name: string;
  storenumber?: string;
}

interface Technician {
  id: number;
  name: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface EditATRModalProps {
  requisitionId: number | null;
  onClose: () => void;
  onSave: (updatedRequisition: RequisitionDetail) => void;
}

interface EditFormData {
  category: number;
  sub_category: number;
  internal: number;
  technician_id: number | null;
  supplier_id: number | null;
  is_repair: number;
  selectedStore: number | null;
  assetsToRepair: string[];
  descriptionOfRepairs: string[];
  selected_equipment: Array<{
    id: number;
    description: string;
    category: string;
    subcategory: string;
    serial_number: string;
  }>;
  equipment_descriptions: string[];
  selected_asset: number;
  requisition_date: string;
  hasQuotation: boolean;
  quotation_date: string;
  repair_cost: number;
  quotation_number: string;
  vat: number;
  attachment: File | null;
}

const EditATRModal: React.FC<EditATRModalProps> = ({ requisitionId, onClose, onSave }) => {
  const dispatch: AppDispatch = useDispatch();

  const [formData, setFormData] = useState<EditFormData>({
    category: 0,
    sub_category: 0,
    internal: 1,
    technician_id: null,
    supplier_id: null,
    is_repair: 1,
    selectedStore: null,
    assetsToRepair: [''],
    descriptionOfRepairs: [''],
    selected_equipment: [],
    equipment_descriptions: [],
    selected_asset: 0,
    requisition_date: '',
    hasQuotation: false,
    quotation_date: '',
    repair_cost: 0,
    quotation_number: '',
    vat: 0,
    attachment: null,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Fetch stores from API (cached)
  const fetchStores = async () => {
    try {
      setLoadingStores(true);
      const data = await fetchReferenceData<Store[]>('stores', '/requisitions/stores');
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      setError('Failed to load stores');
      toast.error('Failed to load stores');
    } finally {
      setLoadingStores(false);
    }
  };

  // Fetch technicians from API (cached)
  const fetchTechnicians = async () => {
    try {
      setLoadingTechnicians(true);
      const data = await fetchReferenceData<Technician[]>('technicians', '/requisitions/technicians');
      setTechnicians(data);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setError('Failed to load technicians');
      toast.error('Failed to load technicians');
    } finally {
      setLoadingTechnicians(false);
    }
  };

  // Fetch suppliers from API (cached)
  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const data = await fetchReferenceData<Supplier[]>('suppliers', '/requisitions/suppliers');
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Failed to load suppliers');
      toast.error('Failed to load suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Fetch categories from API (cached)
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await fetchReferenceData<Category[]>('categories', '/requisitions/categories');
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch subcategories based on selected category
  const fetchSubCategories = async (categoryId: number) => {
    try {
      const response = await apiClient.get(`/requisitions/subcategories?category_id=${categoryId}`);
      setSubCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setError('Failed to load subcategories');
      toast.error('Failed to load subcategories');
    }
  };

  // Fetch equipment based on selected stores and subcategory
  const fetchEquipment = async (storeIds: number[], subcategoryId?: number) => {
    try {
      setLoadingEquipment(true);
      const params = new URLSearchParams();
      storeIds.forEach(id => params.append('store_ids[]', id.toString()));
      if (subcategoryId) {
        params.append('subcategory_id', subcategoryId.toString());
      }
      const response = await apiClient.get(`/requisitions/equipment?${params.toString()}`);
      setEquipment(response.data.data || []);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setError('Failed to load equipment');
      toast.error('Failed to load equipment');
    } finally {
      setLoadingEquipment(false);
    }
  };

  // Load categories and stores when modal opens
  useEffect(() => {
    fetchCategories();
    fetchStores();
    fetchTechnicians();
    fetchSuppliers();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (formData.category > 0) {
      fetchSubCategories(formData.category);
    } else {
      setSubCategories([]);
    }
  }, [formData.category]);

  // Load equipment when category is 10 and store/subcategory change
  useEffect(() => {
    if (formData.category === 10 && formData.selectedStore) {
      fetchEquipment([formData.selectedStore], formData.sub_category || undefined);
    } else {
      setEquipment([]);
    }
  }, [formData.category, formData.selectedStore, formData.sub_category]);

  // Load requisition data when modal opens
  useEffect(() => {
    if (requisitionId) {
      setLoading(true);
      dispatch(fetchRequisitionDetails(requisitionId.toString()))
        .unwrap()
        .then((data) => {
          // Populate form with existing data
          const categoryId = data.category?.id || 0;
          const storeId = data.stores?.[0]?.id || null;
          const subcategoryId = data.subcategory?.id || 0;

          setFormData({
            category: categoryId,
            sub_category: subcategoryId,
            internal: data.internal || 1,
            technician_id: data.technician?.id || null,
            supplier_id: data.supplier?.id || null,
            is_repair: data.is_repair || 1,
            selectedStore: storeId,
            hasQuotation: !!data.quotation,
            assetsToRepair: data.assets?.map(asset => asset.asset) || [''],
            descriptionOfRepairs: data.assets?.map(asset =>
              asset.descriptions?.map(desc => desc.description).join(', ') || ''
            ) || [''],
            selected_equipment: (data.equipment_requests && data.equipment_requests.length > 0
              ? data.equipment_requests.map((req: any) => ({
                  id: req.equipment_id || 0,
                  description: req.equipment?.description || '',
                  category: req.equipment?.category?.name || '',
                  subcategory: req.equipment?.sub_category?.name || '',
                  serial_number: req.equipment?.serial_number || '',
                }))
              : data.equipmentItems?.map(item => ({
                  id: 0,
                  description: item.asset?.replace(/ \(S\/N:.*\)/, '') || '',
                  category: item.equipment?.category || '',
                  subcategory: item.equipment?.subcategory || '',
                  serial_number: item.equipment?.serial_number || '',
                })) || []
            ),
            equipment_descriptions: (data.equipment_requests && data.equipment_requests.length > 0
              ? data.equipment_requests.map((req: any) => req.notes || '')
              : data.equipmentItems?.map(item => item.descriptions?.[0] || '') || []
            ),
            selected_asset: 0,
            requisition_date: data.date || '',
            quotation_date: data.quotation?.date || '',
            repair_cost: data.quotation?.amount || 0,
            quotation_number: data.quotation?.quotation_number || '',
            vat: data.quotation?.vat ? 1 : 0,
            attachment: null,
          });

          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setError('Failed to load requisition details');
          toast.error('Failed to load requisition details');
        });
    }
  }, [requisitionId, dispatch]);

  const handleInputChange = (field: keyof EditFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field: 'assetsToRepair' | 'descriptionOfRepairs' | 'equipment_descriptions', index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    // Validate file type on frontend
    if (file && file.type !== 'application/pdf') {
      setError('Please select a valid PDF file');
      toast.error('Please select a valid PDF file');
      event.target.value = ''; // Clear the input
      return;
    }

    // Validate file size (10MB max)
    if (file && file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      toast.error('File size must be less than 10MB');
      event.target.value = ''; // Clear the input
      return;
    }

    setError(null); // Clear any previous errors
    setFormData(prev => ({
      ...prev,
      attachment: file
    }));
  };

  const addAssetToRepair = () => {
    setFormData(prev => ({
      ...prev,
      assetsToRepair: [...prev.assetsToRepair, ''],
      descriptionOfRepairs: [...prev.descriptionOfRepairs, '']
    }));
  };

  const removeAssetToRepair = (index: number) => {
    setFormData(prev => ({
      ...prev,
      assetsToRepair: prev.assetsToRepair.filter((_, i) => i !== index),
      descriptionOfRepairs: prev.descriptionOfRepairs.filter((_, i) => i !== index)
    }));
  };

  const addSelectedEquipment = () => {
    if (formData.selected_asset > 0) {
      const selectedEquipment = equipment.find(eq => eq.id === formData.selected_asset);
      if (selectedEquipment) {
        // Check for duplicate equipment by equipment ID or serial number
        const isDuplicate = formData.selected_equipment.some(
          existing => existing.id === selectedEquipment.id ||
            (existing.serial_number === selectedEquipment.serial_number && selectedEquipment.serial_number && selectedEquipment.serial_number !== 'N/A')
        );

        if (isDuplicate) {
          setError(`Equipment is already added.`);
          toast.error('Equipment is already added.');
          return;
        }

        setFormData(prev => ({
          ...prev,
          selected_equipment: [...prev.selected_equipment, {
            id: selectedEquipment.id,
            description: selectedEquipment.description,
            category: selectedEquipment.category?.name || '',
            subcategory: selectedEquipment.subCategory?.name || subCategories.find(sc => sc.id === formData.sub_category)?.name || '',
            serial_number: selectedEquipment.serial_number || ''
          }],
          equipment_descriptions: [...prev.equipment_descriptions, ''],
          selected_asset: 0
        }));
      }
    }
  };

  const removeSelectedEquipment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      selected_equipment: prev.selected_equipment.filter((_, i) => i !== index),
      equipment_descriptions: prev.equipment_descriptions.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!requisitionId) return;

    // Basic validation
    if (!formData.category) {
      setError('Please select a category');
      toast.error('Please select a category');
      return;
    }
    if (!formData.sub_category) {
      setError('Please select a sub category');
      toast.error('Please select a sub category');
      return;
    }
    if (!formData.selectedStore) {
      setError('Please select a store');
      toast.error('Please select a store');
      return;
    }
    if (formData.internal === 1 && !formData.technician_id) {
      setError('Please select a technician for internal requisitions');
      toast.error('Please select a technician for internal requisitions');
      return;
    }
    if (formData.internal === 2 && !formData.supplier_id) {
      setError('Please select a supplier for external requisitions');
      toast.error('Please select a supplier for external requisitions');
      return;
    }

    setSaving(true);
    setError(null);

    // Filter out empty asset pairs so backend validation only sees meaningful entries
    const validAssetPairs = formData.assetsToRepair
      .map((asset, index) => ({
        asset: asset.trim(),
        description: (formData.descriptionOfRepairs[index] || '').trim(),
      }))
      .filter(pair => pair.asset !== '' || pair.description !== '');

    const filteredAssets = validAssetPairs.length > 0 ? validAssetPairs.map(pair => pair.asset) : [''];
    const filteredDescriptions = validAssetPairs.length > 0 ? validAssetPairs.map(pair => pair.description) : [''];

    const { selectedStore, ...formDataWithoutStore } = formData;
    const dataToSend = {
      ...formDataWithoutStore,
      selectedStores: selectedStore ? [selectedStore] : [],
      assetsToRepair: filteredAssets,
      descriptionOfRepairs: filteredDescriptions,
    };

    try {
      let response;

      // If there's a file attachment, use FormData
      if (formData.attachment) {
        console.log('Sending with file attachment:', formData.attachment);
        console.log('File type:', formData.attachment.type);
        console.log('File name:', formData.attachment.name);
        console.log('File size:', formData.attachment.size);
        console.log('File instanceof File:', formData.attachment instanceof File);

        const formDataToSend = new FormData();

        // Add all form fields from filtered data
        Object.entries(dataToSend as Record<string, any>).forEach(([key, value]) => {
          if (key === 'attachment' && value instanceof File) {
            console.log('Adding file to FormData:', value);
            console.log('File object details:', {
              name: value.name,
              type: value.type,
              size: value.size,
              lastModified: value.lastModified
            });
            formDataToSend.append('attachment', value);
          } else if (key === 'selectedStores' && Array.isArray(value)) {
            value.forEach((storeId: number) => formDataToSend.append('selectedStores[]', storeId.toString()));
          } else if (key === 'hasQuotation') {
            formDataToSend.append('hasQuotation', value ? '1' : '0');
          } else if (key === 'assetsToRepair' && Array.isArray(value)) {
            value.forEach((asset, index) => {
              formDataToSend.append(`assetsToRepair[${index}]`, asset);
            });
          } else if (key === 'descriptionOfRepairs' && Array.isArray(value)) {
            value.forEach((description, index) => {
              formDataToSend.append(`descriptionOfRepairs[${index}]`, description);
            });
          } else if (key === 'selected_equipment' && Array.isArray(value)) {
            value.forEach((equipment, index) => {
              formDataToSend.append(`selected_equipment[${index}][id]`, equipment.id.toString());
              formDataToSend.append(`selected_equipment[${index}][description]`, equipment.description);
              formDataToSend.append(`selected_equipment[${index}][category]`, equipment.category);
              formDataToSend.append(`selected_equipment[${index}][subcategory]`, equipment.subcategory);
              formDataToSend.append(`selected_equipment[${index}][serial_number]`, equipment.serial_number);
            });
          } else if (key === 'equipment_descriptions' && Array.isArray(value)) {
            value.forEach((description, index) => {
              formDataToSend.append(`equipment_descriptions[${index}]`, description);
            });
          } else if (value !== null && value !== undefined && key !== 'attachment') {
            formDataToSend.append(key, value.toString());
          }
        });

        // Add method spoofing for PUT request with FormData
        formDataToSend.append('_method', 'PUT');

        // Debug: Log FormData contents
        console.log('FormData has attachment:', formDataToSend.has('attachment'));
        console.log('FormData keys:', Array.from(formDataToSend.keys()));

        // Send FormData (remove Content-Type header to let browser set multipart/form-data with boundary)
        response = await apiClient.post(`/requisitions/${requisitionId}`, formDataToSend, {
          headers: {
            'Accept': 'application/json', // Ensure API response is requested
            'Content-Type': undefined, // Let browser set boundary
          },
        });
      } else {
        // No file attachment, send as regular JSON
        const { attachment, ...jsonData } = dataToSend;
        response = await apiClient.post(`/requisitions/${requisitionId}`, {
          ...jsonData,
          _method: 'PUT',
        });
      }

      onSave(response.data);
      onClose();
    } catch (err: any) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);

      // Display all validation errors
      if (err.response?.data?.errors) {
        const errorMessages = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${errorMessages}`);
        toast.error(`Validation errors:\n${errorMessages}`);
      } else {
        setError(err.response?.data?.message || 'Failed to save changes');
        toast.error(err.response?.data?.message || 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!requisitionId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit ATR ID: {requisitionId}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded whitespace-pre-wrap">
                  {error}
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Store
                </label>
                <select
                  value={formData.selectedStore || 0}
                  onChange={(e) => handleInputChange('selectedStore', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  disabled={loadingStores}
                >
                  <option value={0}>Select Store</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    disabled={loadingCategories}
                  >
                    <option value={0}>Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sub Category
                  </label>
                  <select
                    value={formData.sub_category}
                    onChange={(e) => handleInputChange('sub_category', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    disabled={formData.category === 0 || loadingCategories}
                  >
                    <option value={0}>Select Sub Category</option>
                    {subCategories.map((subCategory) => (
                      <option key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Internal/External
                  </label>
                  <select
                    value={formData.internal}
                    onChange={(e) => handleInputChange('internal', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value={1}>Internal</option>
                    <option value={2}>External</option>
                  </select>
                </div>

                {formData.internal === 1 ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Technician
                    </label>
                    <select
                      value={formData.technician_id || 0}
                      onChange={(e) => handleInputChange('technician_id', parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={loadingTechnicians}
                    >
                      <option value={0}>Select Technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Supplier
                    </label>
                    <select
                      value={formData.supplier_id || 0}
                      onChange={(e) => handleInputChange('supplier_id', parseInt(e.target.value) || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={loadingSuppliers}
                    >
                      <option value={0}>Select Supplier</option>
                      {suppliers.map((sup) => (
                        <option key={sup.id} value={sup.id}>
                          {sup.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Repair / New Purchase
                  </label>
                  <select
                    value={formData.is_repair}
                    onChange={(e) => handleInputChange('is_repair', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value={1}>Repair</option>
                    <option value={0}>New Purchase</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Requisition Date
                  </label>
                  <input
                    type="date"
                    max={new Date().toISOString().split('T')[0]}
                    value={formData.requisition_date}
                    onChange={(e) => handleInputChange('requisition_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              {/* Equipment Selection for Category 10 */}
              {formData.category === 10 && formData.selectedStore && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    Selected Equipment
                  </h3>
                  <p className="text-yellow-600 dark:text-yellow-400 mb-3">
                    Total Equipment: {formData.selected_equipment.length}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select Equipment
                      </label>
                      <select
                        value={formData.selected_asset}
                        onChange={(e) => handleInputChange('selected_asset', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={loadingEquipment}
                      >
                        <option value={0}>-- Select Equipment --</option>
                        {equipment.map((eq) => (
                          <option key={eq.id} value={eq.id}>
                            {eq.description} {eq.serial_number ? `(S/N: ${eq.serial_number})` : '(No S/N)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addSelectedEquipment}
                        className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                        disabled={formData.selected_asset === 0}
                      >
                        Add Equipment Pair
                      </button>
                    </div>
                  </div>

                  {formData.selected_equipment.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Subcategory</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Serial Number</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description of Repair</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {formData.selected_equipment.map((equipment, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{equipment.description}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{equipment.category}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{equipment.subcategory}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{equipment.serial_number || 'No S/N'}</td>
                              <td className="px-4 py-2">
                                <textarea
                                  value={formData.equipment_descriptions[index] || ''}
                                  onChange={(e) => handleArrayInputChange('equipment_descriptions', index, e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                  rows={3}
                                  placeholder="Enter repair descriptions (separate multiple with commas)"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  type="button"
                                  onClick={() => removeSelectedEquipment(index)}
                                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
                                  title="Remove equipment"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Assets to Repair - Hidden for equipment category 10 */}
              {formData.category !== 10 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Assets to Repair
                    </h3>
                    <button
                      type="button"
                      onClick={addAssetToRepair}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                    >
                      Add Asset
                    </button>
                  </div>

                <div className="space-y-3">
                  {formData.assetsToRepair.map((asset, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Asset {index + 1}
                        </label>
                        <textarea
                          value={asset}
                          onChange={(e) => handleArrayInputChange('assetsToRepair', index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y"
                          rows={3}
                          placeholder="Enter asset description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description {index + 1}
                        </label>
                        <div className="flex gap-2">
                          <textarea
                            value={formData.descriptionOfRepairs[index] || ''}
                            onChange={(e) => handleArrayInputChange('descriptionOfRepairs', index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y"
                            rows={3}
                            placeholder="Enter repair description"
                          />
                          <button
                            type="button"
                            onClick={() => removeAssetToRepair(index)}
                            className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}

              {/* Quotation Section */}
              <div>
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="hasQuotation"
                    checked={formData.hasQuotation}
                    onChange={(e) => handleInputChange('hasQuotation', e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="hasQuotation" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    I have a quotation
                  </label>
                </div>

                {formData.hasQuotation && (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                      Quotation Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Quotation Date
                        </label>
                        <input
                          type="date"
                          value={formData.quotation_date}
                          onChange={(e) => handleInputChange('quotation_date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Quotation Number
                        </label>
                        <input
                          type="text"
                          value={formData.quotation_number}
                          onChange={(e) => handleInputChange('quotation_number', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="Enter quotation number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Repair Cost
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.repair_cost}
                          onChange={(e) => handleInputChange('repair_cost', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="Enter repair cost"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          VAT
                        </label>
                        <select
                          value={formData.vat}
                          onChange={(e) => handleInputChange('vat', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value={0}>Exclusive</option>
                          <option value={1}>Inclusive</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Attach New Quote (PDF only, max 10MB)
                        </label>
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={handleFileChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-600 dark:file:text-gray-200"
                        />
                        {formData.attachment && (
                          <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                            ✓ Selected: {formData.attachment.name} ({(formData.attachment.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Only PDF files are accepted. Maximum file size: 10MB
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Attached will replace the old quote.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-md transition-colors duration-200"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditATRModal;
